import { useEffect, useRef, useState } from 'react'

import {
  Banner,
  Button,
  Card,
  Dialog,
  Icon,
  Layout,
  Page,
  Tag,
  Text,
} from '@loadsmart/miranda-react'

import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'
import { RequestType } from 'core/domain/RequestType'
import AppLayout from 'core/ui/components/AppLayout'
import Header from 'core/ui/components/Header'
import ZendeskDialog from 'core/ui/components/zendesk/TryNowZendeskDialog'
import { demoLibraryTabLabel } from 'managed-analytics/domain/demoLibrary'
import {
  DEMO_PERFORMANCE_REPORTS,
  groupDemoPerformanceReportsByCategory,
  type DemoPerformanceReport,
} from 'managed-analytics/domain/demoPerformanceReport'
import LibraryToolbar, {
  ALL_REPORTS_TAB,
  type LibraryTab,
} from 'managed-analytics/ui/components/libraryToolbar/LibraryToolbar'
import {
  PreviewFrame,
  ReportThumbnail,
  ThumbnailBar,
  ThumbnailChart,
  ThumbnailColumn,
  ThumbnailLine,
} from 'managed-analytics/ui/PerformanceReportsPage.styles'

const TRACK_EVENT_EXPIRY_MS = 1 * 60 * 1000

// Its own key, so a visit to the Analytics Library does not suppress this
// page's open event.
const LAST_TRACKED_STORAGE_KEY = 'lastTrackedDemoPerformanceReportsEvent'

type ReportPreview = {
  report: DemoPerformanceReport
  url: string
}

const sections = groupDemoPerformanceReportsByCategory(DEMO_PERFORMANCE_REPORTS)

const tabs: Array<LibraryTab> = [
  { id: ALL_REPORTS_TAB, label: 'All reports', count: DEMO_PERFORMANCE_REPORTS.length },
  ...sections.map(([category, reports]) => ({
    id: category,
    label: demoLibraryTabLabel(category),
    count: reports.length,
  })),
]

export default function DemoPerformanceReportsPage() {
  const { track } = useAnalytics()

  const [activeTab, setActiveTab] = useState<string>(ALL_REPORTS_TAB)
  const [preview, setPreview] = useState<ReportPreview | null>(null)
  const [pendingPreview, setPendingPreview] = useState<string | null>(null)
  const [previewError, setPreviewError] = useState('')
  const [requestedReport, setRequestedReport] = useState<DemoPerformanceReport | null>(null)
  // Guards against an earlier "open" resolving after a later one.
  const previewRequestRef = useRef(0)

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview.url)
    }
  }, [preview])

  useEffect(() => {
    function shouldTrackEvent() {
      const lastTracked = localStorage.getItem(LAST_TRACKED_STORAGE_KEY)
      if (!lastTracked) return true

      const now = Date.now()
      return now - parseInt(lastTracked, 10) > TRACK_EVENT_EXPIRY_MS
    }

    if (shouldTrackEvent()) {
      track(AnalyticsEvent.OpenDemoPerformanceReports)
      localStorage.setItem(LAST_TRACKED_STORAGE_KEY, Date.now().toString())
    }
  }, [track])

  const visibleSections =
    activeTab === ALL_REPORTS_TAB
      ? sections
      : sections.filter(([category]) => category === activeTab)

  // A single product's tab is its own heading. Same rule as the library.
  const showSectionHeadings = activeTab === ALL_REPORTS_TAB

  function closePreview() {
    previewRequestRef.current += 1
    setPreview(null)
  }

  /*
    The PDFs are served with `frame-ancestors 'none'`, so an iframe pointed at
    the file's own URL is refused even same-origin. Fetching it and framing a
    blob URL is what the signed-in reports page does, and the page's CSP
    allows `frame-src blob:`.
  */
  async function openPreview(report: DemoPerformanceReport) {
    track(AnalyticsEvent.DemoPerformanceReportView, {
      'Report Name': report.reportName,
      Product: report.category,
    })

    setPreviewError('')
    setPendingPreview(report.id)
    const requestId = (previewRequestRef.current += 1)

    try {
      const response = await fetch(report.pdfUrl)
      if (!response.ok) throw new Error(`Sample responded ${response.status}`)

      const blob = await response.blob()
      if (requestId !== previewRequestRef.current) return

      setPreview({ report, url: URL.createObjectURL(blob) })
    } catch {
      setPreviewError('Unable to open this sample. Try again in a moment.')
    } finally {
      setPendingPreview(current => (current === report.id ? null : current))
    }
  }

  function trackDownload(report: DemoPerformanceReport) {
    track(AnalyticsEvent.DemoPerformanceReportDownload, {
      'Report Name': report.reportName,
      Product: report.category,
    })
  }

  function openRequest(report: DemoPerformanceReport) {
    track(AnalyticsEvent.DemoPerformanceReportRequest, {
      'Report Name': report.reportName,
      Product: report.category,
    })
    setRequestedReport(report)
  }

  return (
    <AppLayout>
      <Header
        unauthCtaLabel="Sign in"
        unauthCtaHref="/"
        title={
          <Text color="color-text-primary" variant="body-md-bold">
            Performance Reports
          </Text>
        }
      />
      <Page.Content>
        <Page.Body>
          <Layout.Stack gap="spacing-6">
            <Text variant="body-md" color="color-text-secondary">
              See what a Loadsmart performance report looks like. Pick your product below, open
              the sample, and request one built from your own operation.
            </Text>

            {previewError && (
              <Banner variant="danger" dismissible onDismiss={() => setPreviewError('')}>
                <Banner.Description>{previewError}</Banner.Description>
              </Banner>
            )}

            <LibraryToolbar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

            <Layout.Stack gap="spacing-6">
              {visibleSections.map(([category, reports]) => (
                <Layout.Stack key={category} gap="spacing-3">
                  {showSectionHeadings && (
                    <Text variant="heading-lg-bold" color="color-text-primary">
                      {category}
                    </Text>
                  )}

                  {reports.map(report => (
                    <Card key={report.id}>
                      <Card.Body>
                        <Layout.Group gap="spacing-5" align="flex-start">
                          <ReportThumbnail aria-hidden="true">
                            <ThumbnailBar />
                            <ThumbnailLine $width="88%" />
                            <ThumbnailLine $width="72%" />
                            <ThumbnailChart>
                              <ThumbnailColumn $height="40%" />
                              <ThumbnailColumn $height="66%" />
                              <ThumbnailColumn $height="52%" />
                              <ThumbnailColumn $height="88%" />
                              <ThumbnailColumn $height="71%" />
                            </ThumbnailChart>
                            <ThumbnailLine $width="80%" />
                            <ThumbnailLine $width="57%" />
                          </ReportThumbnail>

                          <Layout.Stack gap="spacing-3">
                            <Layout.Group align="center" gap="spacing-2">
                              <Text variant="heading-sm-bold" color="color-text-primary">
                                {report.reportName}
                              </Text>
                              <Tag variant="success" size="small">
                                Sample
                              </Tag>
                            </Layout.Group>

                            <Text variant="body-sm" color="color-text-tertiary">
                              {demoLibraryTabLabel(report.category)} · {report.cadence}
                            </Text>

                            <Text variant="body-md" color="color-text-secondary">
                              {report.description}
                            </Text>

                            <Layout.Group gap="spacing-2">
                              <Button
                                variant="primary"
                                loading={pendingPreview === report.id}
                                disabled={pendingPreview === report.id}
                                onClick={() => openPreview(report)}
                              >
                                Open report
                              </Button>
                              <Button
                                variant="secondary"
                                leading={<Icon name="download" />}
                                href={report.pdfUrl}
                                download={report.fileName}
                                onClick={() => trackDownload(report)}
                              >
                                Download PDF
                              </Button>
                              <Button
                                variant="secondary"
                                leading={<Icon name="send" />}
                                onClick={() => openRequest(report)}
                              >
                                Request this report
                              </Button>
                            </Layout.Group>
                          </Layout.Stack>
                        </Layout.Group>
                      </Card.Body>
                    </Card>
                  ))}
                </Layout.Stack>
              ))}
            </Layout.Stack>
          </Layout.Stack>
        </Page.Body>
      </Page.Content>

      <Dialog open={preview !== null} size="full-page" variant="neutral" onClose={closePreview}>
        <Dialog.Header>
          {preview?.report.reportName}
          <Dialog.Close />
        </Dialog.Header>
        <Dialog.Body>
          {preview && <PreviewFrame title={preview.report.reportName} src={preview.url} />}
        </Dialog.Body>
        <Dialog.Actions>
          <Dialog.ActionTertiary
            leading={<Icon name="send" />}
            onClick={() => {
              // Close the preview so the dialogs don't stack.
              const report = preview?.report
              closePreview()
              if (report) openRequest(report)
            }}
          >
            Request this report
          </Dialog.ActionTertiary>
          <Dialog.ActionPrimary onClick={closePreview}>Close</Dialog.ActionPrimary>
        </Dialog.Actions>
      </Dialog>

      {requestedReport && (
        <ZendeskDialog
          requestType={RequestType.NewRequest}
          open
          title={`Request the ${requestedReport.reportName}`}
          description="Tell us where to reach you and we'll get in touch about building this report from your own data."
          onClose={() => setRequestedReport(null)}
        />
      )}
    </AppLayout>
  )
}
